import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "sonner";
import { ApiResponse, ThunkConfig } from "../../types/responseType";
import {
  AcademicCycle,
  Feature,
  SchoolBranch,
  SchoolBranchResponse,
  SchoolBranchSingleResponse,
} from "../../types/schoolBranchType";
import SchoolBranchApis from "../../api/schoolBranchApis";
import customFetch from "../../util/config/axiosConfig";
import { getLocalStorage } from "../../util/manageStorage";

interface SchoolBranchState {
  selectedBranch: {
    schoolBranchId: number | null;
    schoolBranchName: string | null;
    schoolBranchCode: string | null;
  } | null;

  branchDetails: SchoolBranch | null;
  branches: SchoolBranch[];
  isLoading: boolean;
  features: Feature[];
  cycles: AcademicCycle[];
}

const initialState: SchoolBranchState = {
  selectedBranch: null,
  branchDetails: null,
  branches: [],
  isLoading: false,
  features: [],
  cycles: [],
};

export const getSchoolBranches = createAsyncThunk<
  SchoolBranchResponse,
  string,
  ThunkConfig
>("school/getSchoolBranches", async (schoolCode, thunkAPI) => {
  try {
    const response = await SchoolBranchApis.getAllBranches(schoolCode);
    return response as SchoolBranchResponse;
  } catch (error: any) {
    const message = error?.response?.data?.error || "Error fetching branches";
    return thunkAPI.rejectWithValue(message);
  }
});

export const getSchoolBranchByCode = createAsyncThunk<
  SchoolBranchSingleResponse,
  void,
  ThunkConfig
>("school/getBranchByCode", async (_, thunkAPI) => {
  try {
    const branchCode = getLocalStorage("selectedBranchCode");

    if (!branchCode) {
      return thunkAPI.rejectWithValue("Branch code not found");
    }

    const response = await customFetch.get(
      `/api/school-branch/get-by-code/${branchCode}`,
    );

    return response.data;
  } catch (error: any) {
    const message = error?.response?.data?.error || "Error fetching branch";
    return thunkAPI.rejectWithValue(message);
  }
});

export const getBranchFeatures = createAsyncThunk<
  ApiResponse<Feature[]>,
  number,
  ThunkConfig
>("school/getBranchFeatures", async (branchId, thunkAPI) => {
  try {
    const response = await SchoolBranchApis.getBranchFeatures(branchId);
    return response;
  } catch (error: any) {
    const message =
      error?.response?.data?.error || "Error fetching branch features";
    return thunkAPI.rejectWithValue(message);
  }
});

export const fetchAcademicCycles = createAsyncThunk(
  "academicCycles/fetch",
  async (branchId: number) => {
    const response = await SchoolBranchApis.getBranchAcademicCycle(branchId);
    return response.data;
  },
);

const schoolBranchSlice = createSlice({
  name: "schoolBranch",
  initialState,
  reducers: {
    clearBranches: (state) => {
      state.branches = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSchoolBranches.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getSchoolBranches.fulfilled, (state, { payload }) => {
        state.branches = payload.data;
        state.isLoading = false;
      })
      .addCase(getSchoolBranches.rejected, (state, { payload }) => {
        toast.error(payload as string);
        state.isLoading = false;
        state.branches = [];
      })
      .addCase(getSchoolBranchByCode.fulfilled, (state, { payload }) => {
        const b = payload.data;

        state.selectedBranch = {
          schoolBranchId: b.schoolBranchId,
          schoolBranchName: b.schoolBranchName,
          schoolBranchCode: b.schoolBranchCode,
        };

        state.branchDetails = b; // full object stored separately
        state.isLoading = false;
      })

      .addCase(getBranchFeatures.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getBranchFeatures.fulfilled, (state, { payload }) => {
        state.features = payload.data;
        state.isLoading = false;
      })
      .addCase(getBranchFeatures.rejected, (state, { payload }) => {
        toast.error(payload as string);
        state.isLoading = false;
      })
      .addCase(fetchAcademicCycles.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAcademicCycles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cycles = action.payload;
      })
      .addCase(fetchAcademicCycles.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const { clearBranches } = schoolBranchSlice.actions;
export default schoolBranchSlice.reducer;
